import { useApp, useTranslation, useUserInteractions } from "@next-platform/client-common-core";
import { IconVariants, Input, Size, useMedia } from "@next-platform/client-common-spicy-ui";
import { useEffect, useRef, useState } from "react";
import { useHits, useSearchBox } from "react-instantsearch";
import { useDebounceValue } from "usehooks-ts";

import { SearchBarProps } from "./types";

export default function SearchBar({ queryBy, setQueryBy, defaultQueryBy, isCreatorSearchEnabled = true, setFoundNegativeKeyword }: SearchBarProps) {
  const { isMobile } = useMedia();
  const { results } = useHits();
  const { setIsSearchInProgress } = useUserInteractions();
  const isMounted = useRef(false);
  const { refine } = useSearchBox();
  const { negativeKeywords } = useApp();
  const [searchedQuery, setSearchQuery] = useState(results?.query);
  const [debouncedSearchQuery] = useDebounceValue(searchedQuery, 300);
  const { t } = useTranslation();

  useEffect(() => {
    if (results?.query && !isMounted.current) {
      if (results?.query !== "*") {
        setSearchQuery(results?.query);
      }
      isMounted.current = true;
    }
  }, [results?.query]);

  useEffect(() => {
    if (isMounted.current) {
      setIsSearchInProgress(!!(debouncedSearchQuery && debouncedSearchQuery?.length > 0));
      if (searchedQuery !== results?.query) {
        refine(debouncedSearchQuery as string);
      }
    }
  }, [debouncedSearchQuery]);

  const handleInputChange = (value: string) => {
    setSearchQuery(value);

    if (value.startsWith("@") && isCreatorSearchEnabled) {
      if (queryBy === defaultQueryBy) {
        setQueryBy("creator_username");
      }
    } else {
      if (queryBy !== defaultQueryBy) {
        setQueryBy(defaultQueryBy);
      }

      if (negativeKeywords) {
        const newNK = negativeKeywords.some((entry) => entry.alternatives.includes(value.toLowerCase()));
        setFoundNegativeKeyword(newNK);
      }
    }
  };

  return (
    <Input
      value={searchedQuery}
      iconStart={IconVariants.SEARCH}
      onClear={() => setSearchQuery("")}
      isClearable={!!searchedQuery}
      iconEnd={searchedQuery ? IconVariants.CLOSE : undefined}
      placeholder={t("search:placeholder")}
      onChange={handleInputChange}
      size={Size.SMALL}
      width={isMobile ? "100%" : 400}
      className="flex-nowrap"
      debounceTimeout={400}
    />
  );
}
